import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 인앱 브라우저에서 구글 로그인 차단 시 클릭 후 펼쳐지는 안내 컴포넌트
 * @param {Object} props
 * @param {boolean} props.open - 펼침 여부 (false 시 DOM 비렌더링)
 * @param {'android_failed' | 'ios' | 'generic'} props.variant - 안내 유형
 * @param {() => void} props.onClose - 닫기 콜백
 * @param {() => void} props.onForceGoogleLogin - 그래도 구글 로그인 시도 콜백
 */
export default function InAppBrowserNotice({ open, variant, onClose, onForceGoogleLogin }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!open) {
    return null;
  }

  // 주소 복사 핸들러 (1차 clipboard API -> 2차 execCommand 폴백 -> 3차 직접 복사용 input 노출)
  const handleCopyUrl = async () => {
    if (typeof window === 'undefined') return;
    const textToCopy = window.location.href;

    // 1차 시도: navigator.clipboard
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        if (isMountedRef.current) {
          setCopied(true);
          setTimeout(() => {
            if (isMountedRef.current) setCopied(false);
          }, 2000);
        }
        return;
      } catch (_) {
        // 폴백으로 진행
      }
    }

    // 2차 시도: document.execCommand('copy')
    try {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        if (isMountedRef.current) {
          setCopied(true);
          setTimeout(() => {
            if (isMountedRef.current) setCopied(false);
          }, 2000);
        }
        return;
      }
    } catch (_) {
      // 3차 폴백으로 진행
    }

    // 3차 안전망: 복사 API 모두 실패 시 화면에 직접 긁어 복사할 수 있는 input 노출
    if (isMountedRef.current) {
      setCopyFailed(true);
    }
  };

  const currentHref = typeof window !== 'undefined' ? window.location.href : '';

  // variant별 본문 문구 렌더링
  const renderMessage = () => {
    if (variant === 'android_failed') {
      return (
        <p className="text-[12.5px] text-on-surface-variant leading-relaxed break-keep">
          {t('auth.inapp_open_failed')}
        </p>
      );
    }

    if (variant === 'ios') {
      return (
        <div className="flex flex-col gap-1 text-[12.5px] text-on-surface-variant leading-relaxed break-keep">
          <p className="font-medium text-on-surface">{t('auth.inapp_use_browser')}</p>
          <p>{t('auth.inapp_ios_guide')}</p>
        </div>
      );
    }

    // generic (그 외 환경)
    return (
      <div className="flex flex-col gap-1 text-[12.5px] text-on-surface-variant leading-relaxed break-keep">
        <p className="font-medium text-on-surface">{t('auth.inapp_use_browser')}</p>
        <p>{t('auth.inapp_generic_guide')}</p>
      </div>
    );
  };

  return (
    <div className="w-full bg-surface-container-lowest shadow-md border border-outline-variant/60 rounded-xl p-3 pr-8 flex flex-col gap-2 relative mw-notice-in">
      {/* 닫기 버튼 */}
      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="absolute top-2 right-2 p-1.5 rounded-full text-on-surface-variant/70 hover:text-on-surface hover:bg-surface-container transition-colors flex items-center justify-center min-w-[32px] min-h-[32px] cursor-pointer bg-transparent border-0"
      >
        <span className="material-symbols-outlined text-[16px]">close</span>
      </button>

      {/* 본문 안내 */}
      {renderMessage()}

      {/* 주소 복사 버튼 */}
      <button
        type="button"
        onClick={handleCopyUrl}
        className="w-full bg-primary text-on-primary py-2 px-4 rounded-lg text-[13px] font-medium shadow-sm hover:bg-primary/90 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5 cursor-pointer border-0"
      >
        <span className="material-symbols-outlined text-[16px]">{copied ? 'check' : 'content_copy'}</span>
        {copied ? t('auth.copy_link_copied') : t('auth.copy_link')}
      </button>

      {/* 3차 복사 실패 시 수동 복사용 input */}
      {copyFailed && (
        <div className="flex flex-col gap-1 mt-0.5">
          <span className="text-[11px] text-on-surface-variant break-keep">
            {t('auth.copy_manual_guide')}
          </span>
          <input
            type="text"
            readOnly
            value={currentHref}
            onClick={(e) => e.target.select()}
            className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg px-2.5 py-1.5 text-[12px] text-on-surface select-all focus:outline-none"
          />
        </div>
      )}

      {/* 오판 대비 필수: 그래도 구글 로그인 시도 링크 */}
      {typeof onForceGoogleLogin === 'function' && (
        <div className="text-center border-t border-outline-variant/30 pt-1 mt-0.5">
          <button
            type="button"
            onClick={() => onForceGoogleLogin()}
            className="text-[11px] text-on-surface-variant/60 underline hover:text-primary transition-colors bg-transparent border-0 cursor-pointer p-0"
          >
            {t('auth.force_google_login')}
          </button>
        </div>
      )}
    </div>
  );
}
